import { useMemo } from "react";
import { useThemeSlice } from "../../components/ui/StatusBadge.jsx";
import { EntityLoadingState } from "../../components/ui/EntityPageChrome.jsx";
import { compactPrimaryButton } from "../../components/ui/buttonStyles.js";
import {
  PageErrorBanner,
  TypeCatalogCard,
  TypeCatalogListFooter,
  TypeCatalogSectionHeader,
  TypeCatalogToolbar,
} from "../../components/ui/TypeCatalogToolbar.jsx";
import { TypeCatalogEmptyState } from "../../components/emptyStates/TypeCatalogEmptyState.jsx";
import { DeleteConfirmModal } from "../../components/modal/DeleteConfirmModal.jsx";
import { Ico } from "../../components/icons/icons.jsx";
import { useDeleteConfirm } from "../../hooks/useDeleteConfirm.js";
import { useAccountAssetTypes } from "../../logic/hooks/useAccountAssetTypes.js";
import { buildAccountDetailTokens } from "./accountDetailTheme.js";
import { AssetTypeList } from "./components/AssetTypeList.jsx";
import { AssetTypeModal } from "./components/AssetTypeModal.jsx";

export function AccountAssetTypesTab({ account }) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const ap = useMemo(() => buildAccountDetailTokens(dark, c, A), [dark, c, A]);
  const vm = useAccountAssetTypes(account?.id);
  const { requestDelete, deleteModalProps } = useDeleteConfirm();

  const showing = vm.items.length;
  const total = vm.allItems.length;
  const filtered = vm.search.trim().length > 0;
  const countLabel = filtered ? `${showing} of ${total} types` : `${total} type${total === 1 ? "" : "s"}`;

  const handleDelete = (row) => {
    requestDelete({
      title: "Delete asset type",
      description: "This removes the schema definition. Existing assets using this type are not affected in this demo.",
      confirmText: row.name,
      entityLabel: "Asset type",
      onConfirm: () => vm.removeAssetType(row.id),
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
      <TypeCatalogSectionHeader
        title="Asset types"
        subtitle={`Define schemas for assets on ${account?.companyName ?? "this account"} — each type specifies custom fields and data types.`}
        action={
          <button type="button" onClick={vm.openCreate} style={compactPrimaryButton(A)}>
            {Ico.plus}
            Create asset type
          </button>
        }
        c={c}
      />

      <PageErrorBanner message={vm.error} />

      <TypeCatalogCard ap={ap} dark={dark}>
        <TypeCatalogToolbar search={vm.search} onSearchChange={vm.setSearch} placeholder="Search asset types…" countLabel={countLabel} c={c} />

        {vm.loading ? (
          <EntityLoadingState />
        ) : total === 0 ? (
          <TypeCatalogEmptyState c={c} A={A} icon={Ico.model} message="No asset types yet" hint="Create a type to define fields like registration, odometer, or documents." onAction={vm.openCreate} actionLabel="Create asset type" />
        ) : showing === 0 ? (
          <TypeCatalogEmptyState c={c} A={A} icon={Ico.model} message="No matching asset types" hint="Try a different search term." />
        ) : (
          <AssetTypeList items={vm.items} ap={ap} onEdit={vm.openEdit} onDelete={handleDelete} />
        )}

        {!vm.loading && showing > 0 && (
          <TypeCatalogListFooter c={c}>
            Showing {showing} asset type{showing === 1 ? "" : "s"}
            {filtered ? ` matching “${vm.search.trim()}”` : ""}
          </TypeCatalogListFooter>
        )}
      </TypeCatalogCard>

      <AssetTypeModal open={vm.modalOpen} onClose={vm.closeModal} onSave={vm.saveAssetType} editing={vm.editing} saving={vm.saving} />
      <DeleteConfirmModal {...deleteModalProps} />
    </div>
  );
}
